import React from "react";
import {
  Cluster,
  ClusterStats,
  Marker,
  MarkerClusterer,
} from "@googlemaps/markerclusterer";
import { Provider } from "./SearchProvider.types";
import { useEffect, useRef, useState } from "react";

const parser = new DOMParser();

type SearchProviderMapProps = {
  providers: Provider[];
  center?: { lat: number; lng: number };
  onMarkerClick: (Provider) => void;
  selectedProviderId: number | null;
};

const SearchProviderMap: React.FC<SearchProviderMapProps> = ({
  providers,
  center,
  onMarkerClick,
  selectedProviderId,
}) => {
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [previousSelectedId, setPreviousSelectedId] = useState<number | null>();
  const mapDiv = useRef<HTMLDivElement>(null);

  const updateMarkers = async () => {
    if (!googleMap) return;
    if (typeof google.maps === "undefined") return;

    const { AdvancedMarkerElement } = (await google.maps.importLibrary(
      "marker"
    )) as google.maps.MarkerLibrary;

    const infoWindow = new google.maps.InfoWindow({
      content: "",
      disableAutoPan: false,
    });

    // Add some markers to the map.
    const bounds = new google.maps.LatLngBounds();
    const markers = [];

    providers.map((provider, i) => {
      if (!provider.latitude || !provider.longitude) {
        console.warn(
          `Provider ${provider.title} is missing latitude or longitude`
        );

        return;
      }

      const MarkerIcon =
        '<svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.2576 3.93213C15.118 3.93197 12.1052 5.17095 9.87408 7.37978C7.64294 9.58862 6.37374 12.5888 6.34235 15.7282C6.34235 23.8901 16.8426 32.9755 17.2895 33.3627C17.5592 33.5935 17.9026 33.7203 18.2576 33.7203C18.6126 33.7203 18.9559 33.5935 19.2257 33.3627C19.747 32.9755 30.1728 23.8901 30.1728 15.7282C30.1414 12.5888 28.8722 9.58862 26.6411 7.37978C24.4099 5.17095 21.3971 3.93197 18.2576 3.93213Z" fill="#714384" /></svg>';

      const pinSvg = parser.parseFromString(
        MarkerIcon,
        "image/svg+xml"
      ).documentElement;

      const position = new google.maps.LatLng({
        lat: parseFloat(provider.latitude),
        lng: parseFloat(provider.longitude),
      });

      const marker = new AdvancedMarkerElement({
        position: position,
        map: googleMap,
        content: pinSvg,
      });

      bounds.extend(position);

      // markers can only be keyboard focusable when they have click listeners
      // open info window when marker is clicked
      marker.addListener("click", () => {
        setPreviousSelectedId(selectedProviderId);

        infoWindow.setContent(`<strong>${provider.title}</strong>`);
        infoWindow.open(googleMap, marker);

        if (selectedProviderId !== provider.id) {
          onMarkerClick(provider);
        }
      });

      markers[provider.id] = marker;
    });

    const render = (cluster: Cluster, stats: ClusterStats, map): Marker => {
      const ClusterIcon =
        '<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<rect x="0.577419" y="0.577419" width="42.729" height="42.729" rx="21.3645" fill="white"/>' +
        '<rect x="0.577419" y="0.577419" width="42.729" height="42.729" rx="21.3645" stroke="var(--section-feature-color)" stroke-width="1.15484"/>' +
        '<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" stroke-width="1.5" stroke="var(--section-feature-color)" font-size="20" fill="var(--section-feature-color)">' +
        cluster.count.toString() +
        "</text>" +
        "</svg>";

      const clusterSvg = parser.parseFromString(
        ClusterIcon,
        "image/svg+xml"
      ).documentElement;

      const marker = new AdvancedMarkerElement({
        position: cluster.position,
        map: map,
        content: clusterSvg,
      });

      return marker;
    };

    new MarkerClusterer({
      markers: markers,
      map: googleMap,
      renderer: {
        render: render,
      },
    });

    googleMap.fitBounds(bounds);

    (window as any).MARKERS = markers;
  };

  useEffect(() => {
    if (selectedProviderId && selectedProviderId !== previousSelectedId) {
      const markers = (window as any).MARKERS;
      if (!markers) return;

      const marker = markers[selectedProviderId];
      const provider = providers.find((p) => p.id === selectedProviderId);

      if (marker && provider) {
        const position = new google.maps.LatLng({
          lat: parseFloat(provider.latitude),
          lng: parseFloat(provider.longitude),
        });

        googleMap.panTo(position);
        googleMap.setZoom(14);
        // trigger click event to open info window
        google.maps.event.trigger(marker, "click");

        setPreviousSelectedId(selectedProviderId);
      }
    }
  }, [selectedProviderId, providers]);

  useEffect(() => {
    updateMarkers();
  }, [googleMap, providers]);

  useEffect(() => {
    if (!googleMap) return;

    if (center) {
      googleMap.setCenter(center);
    }
  }, [googleMap, center]);

  useEffect(() => {
    if (!mapDiv.current) return;
    if (typeof google === "undefined") return;

    const map = new google.maps.Map(mapDiv.current, {
      zoom: 8,
      center: { lat: -41.2777046, lng: 174.4990015 },
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      controlSize: 24,
      maxZoom: 16,
      minZoom: 7,
      mapId: "searchProviderTool",
    });

    setGoogleMap(map);
  }, [mapDiv]);

  return <div style={{ height: "100%", width: "100%" }} ref={mapDiv} />;
};

export default SearchProviderMap;
